/* eslint-disable unicorn/prefer-array-flat */
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import apiMapper from 'react/signups/card/ApiMapper';
import api from 'react/signups/card/api';
import ClientTrustDetails from 'react/signups/card/modules/trust/trust_details/client/TrustDetails';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import cardAccountTrustShape from 'react/signups/card/shapes/CardAccountTrust';
import organizationShape from 'react/signups/card/shapes/Organization';
import recipeShape from 'react/signups/card/shapes/Recipe';
import userShape from 'react/signups/card/shapes/User';
import { yesNoToBoolean, booleanToYesNo } from 'react/signups/card/utils';

export default function Trust({
  account,
  cardAccountSignup,
  cardAccountTrust,
  organization,
  recipe,
  setCardAccountTrust,
  user,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;

  // -----------------------------------------------------------------------------------------------

  // Arbiters of initial state ---------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------------

  // State hooks -----------------------------------------------------------------------------------

  // Module-level navigation state
  const [moduleComplete, setModuleComplete] = useState(false);

  const [trustDetails, setTrustDetails] = useState({
    trusteeCategory: cardAccountTrust.trusteeCategory || '',
    businessIsTrustee: !cardAccountTrust.trusteeName,
    titleIncludedInList: booleanToYesNo(cardAccountTrust.trusteeTitleMatches) || '',
    trusteeTitle: cardAccountTrust.trusteeTitle || '',
    trustName: cardAccountTrust.trustName || '',
    trusteeName: cardAccountTrust.trusteeName || '',
    address: {
      street1: cardAccountTrust.address?.street1 || '',
      street2: cardAccountTrust.address?.street2 || '',
      city: cardAccountTrust.address?.city || '',
      state: cardAccountTrust.address?.state || '',
      zip: cardAccountTrust.address?.zip || '',
    },
  });

  const [trustDocument, setTrustDocument] = useState({
    attachment: null,
  });

  const cardholderFirstName =
    cardAccountSignup.cardholderFirstName || account.cardholder?.firstName;
  const cardholderLastName = cardAccountSignup.cardholderLastName || account.cardholder?.lastName;

  // -----------------------------------------------------------------------------------------------

  // Effect hooks and memoized callbacks -----------------------------------------------------------

  // Move to next step or mark module complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      moduleComplete,
      setModuleComplete,
      recipe,
      userType,
    });
  }, [location, navigate, userType, cardAccountSignup, moduleComplete, setModuleComplete, recipe]);

  // Send module payload and update Application-level state
  useUpdateEffect(() => {
    if (!moduleComplete) return;

    // Have to use window.FormData in order to send in the attachment
    const formData = new window.FormData();
    formData.append('card_account_trust[documentationVerified]', false);

    if (trustDocument.attachment) {
      formData.append('card_account_trust[attachment]', trustDocument.attachment);
    }

    formData.append(
      'card_account_trust[trusteeTitleMatches]',
      yesNoToBoolean(trustDetails.titleIncludedInList),
    );

    const trusteeName = trustDetails.trusteeName || organization.name;
    formData.append('card_account_trust[trusteeName]', trusteeName);

    formData.append(
      'card_account_trust[trustBeneName]',
      `${cardholderFirstName} ${cardholderLastName}`,
    );

    formData.append('card_account_trust[trustName]', trustDetails.trustName);

    const trustDetailsBody = {
      trusteeTitle: trustDetails.trusteeTitle,
      trusteeCategory: trustDetails.trusteeCategory,
    };

    const addressBody = {
      street1: trustDetails.address.street1,
      street2: trustDetails.address.street2,
      city: trustDetails.address.city,
      state: trustDetails.address.state,
      zip: trustDetails.address.zip,
    };

    Object.keys(trustDetailsBody).forEach((key) => {
      if (trustDetails[key]) {
        formData.append(`card_account_trust[${key}]`, trustDetails[key]);
      }
    });

    Object.keys(addressBody).forEach((key) => {
      if (trustDetails.address[key]) {
        return formData.append(`card_account_trust[address][${key}]`, trustDetails.address[key]);
      }
    });

    if (cardAccountTrust.id) {
      api.updateCardAccountTrust(cardAccountTrust.id, formData).then((response) => {
        setCardAccountTrust(
          apiMapper.mapServerCardAccountTrustToClientCardAccountTrust(response.data.data),
        );
      });
    } else {
      api.createCardAccountTrust(account.id, formData).then((response) => {
        setCardAccountTrust(
          apiMapper.mapServerCardAccountTrustToClientCardAccountTrust(response.data.data),
        );
      });
    }
  }, [moduleComplete]);

  // Navigate to next step when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    onNavigate();
  }, [trustDetails, trustDocument]);

  // Navigate to next module when Application-level state changes in response to api success
  useUpdateEffect(() => {
    onNavigate();
  }, [cardAccountTrust]);

  // -----------------------------------------------------------------------------------------------

  // Pass-down state, props and handlers -----------------------------------------------------------

  const passDownValues = {
    firstName: cardholderFirstName,
    moduleComplete,
    organizationName: organization.name,
    organizationAddress: organization.address,
    attachmentName: cardAccountTrust.attachmentName,
    trustDetails,
    trustDocument,
    adminName: `${user.firstName} ${user.lastName}`,
  };

  const passDownHandlers = {
    setTrustDetails,
    setTrustDocument,
  };

  return (
    <Routes>
      <Route
        element={<ClientTrustDetails {...passDownValues} {...passDownHandlers} />}
        path="/trust_details"
      />
      <Route
        element={
          <Navigate
            replace
            to={userType === USER_TYPES.MYSELF ? '/card_order/card_program' : recipe.defaultPath}
          />
        }
        path="/*"
      />
    </Routes>
  );
}

Trust.propTypes = {
  account: PropTypes.object,
  cardAccountSignup: cardAccountSignupShape.isRequired,
  cardAccountTrust: cardAccountTrustShape,
  organization: organizationShape.isRequired,
  recipe: recipeShape.isRequired,
  setCardAccountTrust: PropTypes.func.isRequired,
  user: userShape.isRequired,
};
